import React, { useMemo, useState, useEffect, memo, useCallback, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Flex, Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { ReactionSummary } from '@atlaskit/reactions';
import type { EmojiId } from '@atlaskit/emoji/types';
import EmojiAddIcon from '@atlaskit/icon/core/emoji-add';
import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import { EmojiResource } from '@atlaskit/emoji/resource';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { Reactions } from '@confluence/reactions/entry-points/Reactions.ts';
import type {
	ReactionLocation,
	Reaction,
} from '@confluence/reactions/entry-points/reactionTypes.ts';
import { ReactionContainerType } from '@confluence/reactions/entry-points/reactionTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { getEmojiProvider } from '@confluence/fabric-providers/entry-points/EmojiProvider.ts';

import { DEFAULT_REACTIONS, EMOJI_SIZE } from './reactions/emojiConfig.ts';
import { DefaultReactionButton } from './reactions/DefaultReactionButton.tsx';
const DEFAULT_EMOJI_COUNT = 5;
import {
	ReactionSummaryTrigger,
	ReactionSummaryPopupContent,
} from './reactions/ReactionSummaryPopup.tsx';
import { useReactionMutations } from './reactions/useReactionMutations.ts';
import { useEmojiPickerDetection } from './reactions/useEmojiPickerDetection.ts';
import { BloomAnimationEmoji } from './reactions/BloomAnimationEmoji.tsx';
import { useBloomAnimation } from './reactions/useBloomAnimation.ts';

const OMNIBAR_ANALYTICS_SOURCE = 'confluenceOmniBar';

const i18n = defineMessages({
	addReaction: {
		id: 'experiment-omnibar.reactions.more-reactions',
		defaultMessage: 'More reactions',
		description: 'Accessible label for the more reactions button in the OmniBar',
	},
});

const styles = cssMap({
	reactionsContainer: {
		display: 'flex',
		alignItems: 'center',
		width: 'auto',
		minWidth: '224px',
		height: '32px',
		gap: '0',
		justifyContent: 'flex-start',
		flexShrink: 0,
		pointerEvents: 'auto',
		borderRadius: token('radius.small'),
		overflow: 'visible',
	},
	emojiPickerWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '32px',
		height: '32px',
		pointerEvents: 'auto',
	},
	separator: {
		width: '1px',
		minWidth: '1px',
		height: '24px',
		marginLeft: token('space.100'),
		marginRight: token('space.100'),
		flexShrink: 0,
		alignSelf: 'center',
		display: 'block',
	},
	summaryContainer: {
		display: 'flex',
		alignItems: 'center',
		height: '32px',
		pointerEvents: 'auto',
	},
});

const SEPARATOR_BORDER_STYLE: React.CSSProperties = {
	borderLeft: `${token('border.width')} solid ${token('color.border')}`,
};

const DEFAULT_EMOJI_IDS = new Set(DEFAULT_REACTIONS.map((e) => e.id));

const EMPTY_QUICK_REACTION_IDS: EmojiId[] = [];

const createReactionsMap = (reactions: Reaction[] | undefined): Map<string, Reaction> => {
	const map = new Map<string, Reaction>();
	reactions?.forEach((reaction) => {
		map.set(reaction.emojiId, reaction);
	});
	return map;
};

const getTopReactions = (reactions: Reaction[] | undefined, count: number = 3): Reaction[] => {
	if (!reactions) {
		return [];
	}
	const reactionsWithCount = reactions.filter((reaction) => reaction.count > 0);
	const sorted = [...reactionsWithCount].sort((a, b) => b.count - a.count);
	return sorted.slice(0, count);
};

const convertToReactionSummaries = (
	reactions: Reaction[] | undefined,
	spaceId: string,
	contentId: string,
): ReactionSummary[] => {
	if (!reactions) {
		return [];
	}
	const result: ReactionSummary[] = [];
	for (const reaction of reactions) {
		if (reaction.count > 0) {
			result.push({
				emojiId: reaction.emojiId,
				count: reaction.count,
				reacted: reaction.reacted || false,
				ari: reaction.ari || `${spaceId}|${contentId}|${reaction.emojiId}`,
				containerAri: reaction.containerAri || spaceId || '',
			});
		}
	}
	return result;
};

interface ReactionsDataProp {
	reactions: Reaction[];
	totalCount: number;
	ari: string;
	containerAri: string;
}

interface OmniBarReactionsProps {
	contentId: string;
	spaceId: string;
	contentType: string;
	contentSubType?: string;
	emojiCount?: number;
	reactionsData: ReactionsDataProp;
	isLoading?: boolean;
	shouldCloseInteractions?: boolean;
	/** When false, bloom animation is not shown (e.g. when omnibar is hidden at bottom of page) */
	shouldShowBloom?: boolean;
	onInteractionStateChange?: (hasActiveInteraction: boolean) => void;
}

const OmniBarReactionsInner: React.FC<OmniBarReactionsProps> = ({
	contentId,
	spaceId,
	contentType,
	contentSubType,
	emojiCount = DEFAULT_EMOJI_COUNT,
	reactionsData,
	isLoading = false,
	shouldCloseInteractions = false,
	shouldShowBloom = true,
	onInteractionStateChange,
}) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition, cloudId, userId } = useSessionData();
	const { Popup } = useChoreographerAssets();

	const emojiProviderPromise = useMemo(
		() =>
			getEmojiProvider(EmojiResource, {
				cloudId: cloudId || '',
				userId: userId || undefined,
				disableUpload: true,
			}),
		[cloudId, userId],
	);

	const reactionsMap = useMemo(
		() => createReactionsMap(reactionsData?.reactions),
		[reactionsData?.reactions],
	);

	const topReactions = useMemo(
		() => getTopReactions(reactionsData?.reactions, 3),
		[reactionsData?.reactions],
	);

	const { handleDefaultReactionClick, handleSummaryReactionClick } = useReactionMutations({
		contentId,
		spaceId,
		contentType,
		contentSubType,
		cloudId,
		edition: edition ?? undefined,
		reactionsMap,
	});

	const [isSummaryPopupOpen, setIsSummaryPopupOpen] = useState(false);
	const isEmojiPickerOpen = useEmojiPickerDetection();

	const { animatingEmojis, triggerBloomAnimation } = useBloomAnimation();
	const summaryTriggerRef = useRef<HTMLButtonElement | null>(null);
	const previousReactedStateRef = useRef<Set<string>>(new Set());
	const previousPickerOpenRef = useRef(false);
	const pickerWasOpenRecentlyRef = useRef(false);
	const isInitialLoadRef = useRef(true);

	useEffect(() => {
		if (isEmojiPickerOpen) {
			pickerWasOpenRecentlyRef.current = true;
		} else if (previousPickerOpenRef.current && !isEmojiPickerOpen) {
			pickerWasOpenRecentlyRef.current = true;
			const timeoutId = setTimeout(() => {
				pickerWasOpenRecentlyRef.current = false;
			}, 1000);
			return () => {
				clearTimeout(timeoutId);
			};
		}
		previousPickerOpenRef.current = isEmojiPickerOpen;
	}, [isEmojiPickerOpen]);

	useEffect(() => {
		if (!reactionsData?.reactions) return;

		const currentReactedEmojis = new Set<string>();
		reactionsData.reactions.forEach((r) => {
			if (r.reacted) {
				currentReactedEmojis.add(r.emojiId);
			}
		});

		if (isInitialLoadRef.current) {
			isInitialLoadRef.current = false;
			previousReactedStateRef.current = currentReactedEmojis;
			return;
		}

		const previousReactedEmojis = previousReactedStateRef.current;
		const pickerWasOpen = isEmojiPickerOpen || pickerWasOpenRecentlyRef.current;

		const newReactions: typeof reactionsData.reactions = [];
		for (const r of reactionsData.reactions) {
			if (r.reacted && !DEFAULT_EMOJI_IDS.has(r.emojiId) && !previousReactedEmojis.has(r.emojiId)) {
				newReactions.push(r);
			}
		}

		if (shouldShowBloom && pickerWasOpen && newReactions.length > 0 && summaryTriggerRef.current) {
			const rect = summaryTriggerRef.current.getBoundingClientRect();
			const x = rect.left + rect.width / 2;
			const y = rect.top;

			newReactions.forEach((reaction) => {
				triggerBloomAnimation(x, y, reaction.emojiId);
			});
		}

		previousReactedStateRef.current = currentReactedEmojis;
	}, [reactionsData, isEmojiPickerOpen, triggerBloomAnimation, shouldShowBloom]);

	const totalCount = reactionsData?.totalCount || 0;
	const shouldShowSummary = totalCount >= 1;

	const closeEmojiPicker = useCallback((pickerIsOpen: boolean) => {
		if (typeof document === 'undefined' || !pickerIsOpen) return;

		document.dispatchEvent(
			new KeyboardEvent('keydown', {
				key: 'Escape',
				code: 'Escape',
				bubbles: true,
				cancelable: true,
			}),
		);

		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur();
		}

		const triggerButton = document.querySelector(
			'[data-testid="reactionPicker-testid"] button',
		) as HTMLButtonElement | null;
		if (triggerButton) {
			triggerButton.click();
		}
	}, []);

	useEffect(() => {
		if (shouldCloseInteractions) {
			setIsSummaryPopupOpen(false);
			const pickerOpen = isEmojiPickerOpen;
			requestAnimationFrame(() => {
				closeEmojiPicker(pickerOpen);
			});
		}
	}, [shouldCloseInteractions, closeEmojiPicker, isEmojiPickerOpen]);

	useEffect(() => {
		if (!shouldShowSummary) {
			setIsSummaryPopupOpen(false);
		}
	}, [shouldShowSummary]);

	// Notify parent when interaction state changes
	useEffect(() => {
		onInteractionStateChange?.(isEmojiPickerOpen || isSummaryPopupOpen);
	}, [isEmojiPickerOpen, isSummaryPopupOpen, onInteractionStateChange]);

	const visibleReactions = useMemo(() => DEFAULT_REACTIONS.slice(0, emojiCount), [emojiCount]);

	const allReactionsForModal = useMemo(
		() => convertToReactionSummaries(reactionsData?.reactions, spaceId, contentId),
		[reactionsData?.reactions, spaceId, contentId],
	);

	const emojiClickHandlers = useMemo(() => {
		const handlers: Record<string, () => void> = {};
		for (const emoji of DEFAULT_REACTIONS) {
			const emojiId = emoji.id || '';
			handlers[emojiId] = () => handleDefaultReactionClick(emojiId);
		}
		return handlers;
	}, [handleDefaultReactionClick]);

	const handlePopupClose = useCallback(() => setIsSummaryPopupOpen(false), []);

	const handleSummaryTriggerClick = useCallback(() => {
		if (!isLoading && totalCount > 0) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'omniBarReactionSummary',
					source: OMNIBAR_ANALYTICS_SOURCE,
				},
			}).fire();
			setIsSummaryPopupOpen((prev) => !prev);
		}
	}, [isLoading, totalCount, createAnalyticsEvent]);

	const renderPopupContent = useCallback(
		() => (
			<ReactionSummaryPopupContent
				reactions={allReactionsForModal}
				emojiProvider={emojiProviderPromise}
				onReactionClick={handleSummaryReactionClick}
			/>
		),
		[allReactionsForModal, emojiProviderPromise, handleSummaryReactionClick],
	);

	const customAnalyticsAttributes = useMemo(
		() => ({ edition, reactionsPlacement: 'omni-bar' }),
		[edition],
	);

	const addReactionLabel = formatMessage(i18n.addReaction);
	const reactionPickerTriggerIcon = useMemo(
		() => <EmojiAddIcon label={addReactionLabel} color={token('color.icon.subtle')} />,
		[addReactionLabel],
	);

	const renderTrigger = useCallback(
		(triggerProps: React.ComponentProps<'button'>) => {
			const { ref: popupRef, ...restProps } = triggerProps;

			const handleRef = (node: HTMLButtonElement | null) => {
				summaryTriggerRef.current = node;
				if (popupRef) {
					if (typeof popupRef === 'function') {
						popupRef(node);
					}
				}
			};

			return (
				<ReactionSummaryTrigger
					{...restProps}
					ref={handleRef}
					isOpen={isSummaryPopupOpen}
					isLoading={isLoading}
					totalCount={totalCount}
					topReactions={topReactions}
					emojiProvider={emojiProviderPromise}
					onClick={handleSummaryTriggerClick}
					cloudId={cloudId ?? undefined}
				/>
			);
		},
		[
			isSummaryPopupOpen,
			isLoading,
			totalCount,
			topReactions,
			emojiProviderPromise,
			handleSummaryTriggerClick,
			cloudId,
		],
	);

	return (
		<Flex xcss={styles.reactionsContainer}>
			{visibleReactions.map((emoji) => {
				const emojiId = emoji.id || '';
				const existingReaction = reactionsMap.get(emojiId);
				return (
					<DefaultReactionButton
						key={emojiId}
						emojiId={emoji}
						emojiProvider={emojiProviderPromise}
						reacted={existingReaction?.reacted || false}
						onClick={emojiClickHandlers[emojiId]}
						tooltipDescriptor={emoji.tooltipDescriptor}
						cloudId={cloudId ?? undefined}
					/>
				);
			})}

			<Box xcss={styles.emojiPickerWrapper} data-emoji-picker-open={isEmojiPickerOpen || undefined}>
				<Reactions
					contentId={contentId}
					location={contentType as ReactionLocation}
					containerId={spaceId}
					containerType={ReactionContainerType.SPACE}
					contentSubType={contentSubType}
					customAnalyticsAttributes={customAnalyticsAttributes}
					onlyRenderPicker
					pickerQuickReactionEmojiIds={EMPTY_QUICK_REACTION_IDS}
					useDefaultStyledContainer
					isSubtle
					hidePlaceholder
					showAddReactionText={false}
					hideNoReactionsText
					reactionPickerTriggerIcon={reactionPickerTriggerIcon}
				/>
			</Box>

			{shouldShowSummary && (
				<>
					<Box
						xcss={styles.separator}
						style={SEPARATOR_BORDER_STYLE}
						testId="omni-bar-reaction-separator"
					/>
					<Popup
						placement="top-start"
						content={renderPopupContent}
						isOpen={isSummaryPopupOpen}
						onClose={handlePopupClose}
						trigger={renderTrigger}
						offset={[0, 8]}
					/>
				</>
			)}

			{typeof document !== 'undefined' && animatingEmojis.length > 0 && (
				<Portal zIndex={layers.spotlight()}>
					{animatingEmojis.map((animatingEmoji) => (
						<BloomAnimationEmoji
							key={animatingEmoji.id}
							animatingEmoji={animatingEmoji}
							emojiProvider={emojiProviderPromise}
							emojiSize={EMOJI_SIZE}
						/>
					))}
				</Portal>
			)}
		</Flex>
	);
};

export const OmniBarReactions = memo(OmniBarReactionsInner);
OmniBarReactions.displayName = 'OmniBarReactions';
